import { useLayoutEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { ReactComponent as CalendarIcon } from "../../public/calender.svg";
import { ReactComponent as NoteIcon } from "../../public/note.svg";
import { ReactComponent as FireIcon } from "../../public/fire.svg";
import { ReactComponent as IceIcon } from "../../public/ice.svg";
import cssText from "./XoSoTruyenThongV2Dark.shadow.css";
import useTruyenThongPane from "./useTruyenThongPane";
import w400Viet from "./fonts/QdVPSTAyLFyeg_IDWvOJmVES_Hw4BXoKZA.woff2";
import w400Ext from "./fonts/QdVPSTAyLFyeg_IDWvOJmVES_Hw5BXoKZA.woff2";
import w400Latin from "./fonts/QdVPSTAyLFyeg_IDWvOJmVES_Hw3BXo.woff2";
import w700Viet from "./fonts/QdVMSTAyLFyeg_IDWvOJmVES_HSMIG86Rb0bcw.woff2";
import w700Ext from "./fonts/QdVMSTAyLFyeg_IDWvOJmVES_HSMIG87Rb0bcw.woff2";
import w700Latin from "./fonts/QdVMSTAyLFyeg_IDWvOJmVES_HSMIG81Rb0.woff2";
import w800Viet from "./fonts/QdVMSTAyLFyeg_IDWvOJmVES_HSQI286Rb0bcw.woff2";
import w800Ext from "./fonts/QdVMSTAyLFyeg_IDWvOJmVES_HSQI287Rb0bcw.woff2";
import w800Latin from "./fonts/QdVMSTAyLFyeg_IDWvOJmVES_HSQI281Rb0.woff2";

const VIET_RANGE = "U+0102-0103, U+0110-0111, U+0128-0129, U+0168-0169, U+01A0-01A1, U+01AF-01B0, U+0300-0301, U+0303-0304, U+0308-0309, U+0323, U+0329, U+1EA0-1EF9, U+20AB";
const EXT_RANGE = "U+0100-02BA, U+02BD-02C5, U+02C7-02CC, U+02CE-02D7, U+02DD-02FF, U+0304, U+0308, U+0329, U+1D00-1DBF, U+1E00-1E9F, U+1EF2-1EFF, U+2020, U+20A0-20AB, U+20AD-20C4, U+2113, U+2C60-2C7F, U+A720-A7FF";
const LATIN_RANGE = "U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD";

const FONT_FACES = [
  [400, w400Viet, VIET_RANGE],
  [400, w400Ext, EXT_RANGE],
  [400, w400Latin, LATIN_RANGE],
  [700, w700Viet, VIET_RANGE],
  [700, w700Ext, EXT_RANGE],
  [700, w700Latin, LATIN_RANGE],
  [800, w800Viet, VIET_RANGE],
  [800, w800Ext, EXT_RANGE],
  [800, w800Latin, LATIN_RANGE],
];

const FONT_CSS = FONT_FACES.map(([weight, url, range]) => `@font-face{font-family:"Be Vietnam Pro";font-style:normal;font-weight:${weight};font-display:swap;src:url("${url}") format("woff2");unicode-range:${range};}`).join("");

const TABS = [
  { id: "mb", label: "Miền Bắc" },
  { id: "mn", label: "Miền Nam" },
  { id: "mt", label: "Miền Trung" },
];

const FREQ_HEADS = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];

const CARD_ICONS = {
  fire: FireIcon,
  ice: IceIcon,
};

const NOTE =
  "Kết quả tự động cập nhật theo giờ quay: miền Nam 16:15 · miền Trung 17:15 · miền Bắc 18:15. Vui lòng đối chiếu với kết quả chính thức của công ty xổ số.";

const HOST_STYLE = {
  all: "initial",
  display: "block",
  width: "100%",
  maxWidth: "100%",
  margin: "0 0 24px",
  padding: 0,
  border: 0,
  background: "transparent",
  boxSizing: "border-box",
  lineHeight: "normal",
  color: "canvastext",
  font: "initial",
};

function rowClass(cls) {
  return cls.replace("kbwxs-", "xstt-");
}

function PrizeTable({ pane }) {
  const multi = pane.kqHeads.length > 0;

  return (
    <div className={`xstt-main${multi ? " xstt-multi" : ""}`}>
      <div className={multi ? "xstt-scroll" : undefined}>
        <table className="xstt-tb xstt-kq">
          {multi ? (
            <thead>
              <tr>
                {pane.kqHeads.map((head) => (
                  <th key={head}>{head}</th>
                ))}
              </tr>
            </thead>
          ) : null}
          <tbody>
            {pane.kq.map((row) => (
              <tr key={row.label} className={rowClass(row.cls)}>
                <th>{row.label}</th>
                {row.cols.map((col, index) => (
                  <td key={`${row.label}-${index}`}>
                    {col.map((value, valueIndex) => (
                      <span key={`${value}-${valueIndex}`}>{value}</span>
                    ))}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className={multi ? "xstt-scroll" : undefined}>
        <table className="xstt-tb xstt-loto">
          <thead>
            <tr>
              {pane.lotoHeads.map((head) => (
                <th key={head}>{head}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pane.loto.map((row) => (
              <tr key={row.head}>
                <th>{row.head}</th>
                {row.cols.map((col, index) => (
                  <td key={`${row.head}-${index}`}>
                    {col.map((value, valueIndex) => (
                      <span key={`${value}-${valueIndex}`}>{value}</span>
                    ))}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stats({ pane }) {
  return (
    <div className="xstt-stat">
      <div className="xstt-sthead">{pane.statHeads[0]}</div>
      <div className="xstt-grid">
        {pane.cards.map((card) => {
          const Icon = CARD_ICONS[card.icon];
          return (
            <div className="xstt-card" key={card.title}>
              <h4>
                {Icon ? <Icon className="xstt-ico" aria-hidden="true" /> : null}
                {card.title}
              </h4>
              <div className="xstt-chips">
                {card.chips.map((chip) => (
                  <span key={`${chip.num}-${chip.label}`}>
                    <b>{chip.num}</b>
                    {chip.label}
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <div className="xstt-scroll">
        <table className="xstt-tb xstt-freq">
          <thead>
            <tr>
              <th>Số</th>
              {FREQ_HEADS.map((head) => (
                <th key={head}>{head}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pane.freq.map((row) => (
              <tr key={row.label}>
                <th>{row.label}</th>
                {row.cells.map((cell, index) => (
                  <td key={`${row.label}-${index}`}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="xstt-sthead">{pane.statHeads[1]}</div>
      {pane.dbgrid.length ? (
        <div className="xstt-dbgrid">
          {pane.dbgrid.map((item, index) => (
            <span key={`${item.date}-${index}`}>
              <small>{item.date}</small>
              {item.prefix}
              <b>{item.tail}</b>
            </span>
          ))}
        </div>
      ) : (
        <div className="xstt-scroll">
          <table className="xstt-tb xstt-dbh">
            <tbody>
              {pane.dbh.map((row) => (
                <tr key={row.date}>
                  <th>{row.date}</th>
                  <td>
                    {row.stations.map((station) => (
                      <span key={`${row.date}-${station.name}`}>
                        <small>{station.name}</small> {station.prefix}
                        <b>{station.tail}</b>
                      </span>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function XoSoTruyenThongV2Dark() {
  const [region, setRegion] = useState("mb");
  const [stats, setStats] = useState(false);
  const pane = useTruyenThongPane(region, stats);

  return (
    <div className="xstt-v2">
      <div className="xstt-top">
        <div className="xstt-tabs" role="tablist">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`xstt-tab${region === tab.id ? " on" : ""}`}
              onClick={() => setRegion(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="xstt-act">
          <span className="xstt-date">
            <CalendarIcon className="xstt-ico" aria-hidden="true" />
            <span>{pane.date}</span>
          </span>
          <button type="button" className="xstt-stbtn" onClick={() => setStats((value) => !value)}>
            <NoteIcon className="xstt-ico" aria-hidden="true" />
            {stats ? "Xem kết quả" : "Thống kê 30 ngày"}
          </button>
        </div>
      </div>

      {stats ? <Stats pane={pane} /> : <PrizeTable pane={pane} />}

      <div className="xstt-note">
        <NoteIcon className="xstt-ico" aria-hidden="true" />
        <span>{NOTE}</span>
      </div>
    </div>
  );
}

function XoSoTruyenThongV2DarkHost() {
  const hostRef = useRef(null);
  const rootRef = useRef(null);

  useLayoutEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;

    let fontStyle = document.getElementById("xstt-be-vietnam-pro");
    if (!fontStyle) {
      fontStyle = document.createElement("style");
      fontStyle.id = "xstt-be-vietnam-pro";
      fontStyle.textContent = FONT_CSS;
      document.head.appendChild(fontStyle);
    }

    const shadow = host.shadowRoot ?? host.attachShadow({ mode: "open" });

    let style = shadow.querySelector("style[data-xstt]");
    if (!style) {
      style = document.createElement("style");
      style.setAttribute("data-xstt", "");
      shadow.appendChild(style);
    }
    style.textContent = cssText;

    let mount = shadow.querySelector("[data-xstt-mount]");
    if (!mount) {
      mount = document.createElement("div");
      mount.setAttribute("data-xstt-mount", "");
      shadow.appendChild(mount);
    }

    if (!rootRef.current) rootRef.current = createRoot(mount);
    rootRef.current.render(<XoSoTruyenThongV2Dark />);

    return () => {
      rootRef.current?.unmount();
      rootRef.current = null;
    };
  }, [cssText]);

  return <div ref={hostRef} className="xstt-v2-host" style={HOST_STYLE} />;
}

export default XoSoTruyenThongV2DarkHost;
